(() => {
  const DESKTOP = "(min-width: 901px)";

  const setup = (root) => {
    const container = root.querySelector("[data-collection-grid]");
    if (!container || container.dataset.gridReady === "true") return;
    container.dataset.gridReady = "true";

    const sectionId = container.dataset.sectionId;
    let controller = null;
    let lastFocused = null;

    const drawer = () => container.querySelector("[data-filter-drawer]");
    const trigger = () => container.querySelector("[data-filter-open]");
    const drawerIsOpen = () => {
      const el = drawer();
      return el !== null && el.classList.contains("is-open");
    };

    const openDrawer = () => {
      const el = drawer();
      if (!el) return;
      lastFocused = document.activeElement;
      el.classList.add("is-open");
      document.body.classList.add("filter-drawer-open");
      const button = trigger();
      if (button) button.setAttribute("aria-expanded", "true");
      const closeButton = el.querySelector(".filter-drawer__close");
      if (closeButton) closeButton.focus();
    };

    const closeDrawer = ({ restoreFocus = true } = {}) => {
      const el = drawer();
      if (!el) return;
      el.classList.remove("is-open");
      document.body.classList.remove("filter-drawer-open");
      const button = trigger();
      if (button) button.setAttribute("aria-expanded", "false");
      const target = lastFocused && lastFocused.isConnected ? lastFocused : button;
      lastFocused = null;
      if (restoreFocus && target) target.focus();
    };

    const render = async (url, { push = true, scroll = false } = {}) => {
      if (controller) controller.abort();
      const myController = new AbortController();
      controller = myController;
      container.classList.add("is-loading");

      try {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set("section_id", sectionId);
        const response = await fetch(fetchUrl, { signal: myController.signal });
        const fresh = new DOMParser()
          .parseFromString(await response.text(), "text/html")
          .querySelector("[data-collection-grid]");
        if (!fresh) throw new Error("Section markup missing from response");

        const keepDrawer = drawerIsOpen();
        const keepGroups = [...container.querySelectorAll("[data-filter-group][open]")].map(
          (group) => group.dataset.filterGroup
        );
        const focusWasInside = container.contains(document.activeElement);

        container.innerHTML = fresh.innerHTML;

        keepGroups.forEach((id) => {
          const group = container.querySelector(`[data-filter-group="${CSS.escape(id)}"]`);
          if (group) group.open = true;
        });

        const el = drawer();
        if (keepDrawer && el) {
          // Re-open without the slide-in transition so the swap is invisible.
          el.classList.add("no-transition", "is-open");
          const button = trigger();
          if (button) button.setAttribute("aria-expanded", "true");
          requestAnimationFrame(() => el.classList.remove("no-transition"));
          if (focusWasInside) el.querySelector(".filter-drawer__panel").focus();
        } else {
          document.body.classList.remove("filter-drawer-open");
          if (focusWasInside) {
            container.tabIndex = -1;
            container.focus({ preventScroll: true });
          }
        }

        if (push) window.history.pushState({}, "", url);
        if (scroll) container.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (error) {
        // A full navigation is the safe fallback for anything but a superseded request.
        if (error.name !== "AbortError") window.location.href = url;
      } finally {
        if (controller === myController) container.classList.remove("is-loading");
      }
    };

    container.addEventListener("click", (event) => {
      if (event.target.closest("[data-filter-open]")) {
        openDrawer();
        return;
      }
      if (event.target.closest("[data-filter-close]")) {
        closeDrawer();
        return;
      }
      const link = event.target.closest("a[data-filter-link], .custom-pagination a[href]");
      if (!link) return;
      event.preventDefault();
      render(link.href, { push: true, scroll: link.closest(".custom-pagination") !== null });
    });

    // Only one desktop dropdown open at a time.
    container.addEventListener(
      "toggle",
      (event) => {
        const opened = event.target;
        if (!opened.matches(".filter-group--bar[open]")) return;
        container.querySelectorAll(".filter-group--bar[open]").forEach((group) => {
          if (group !== opened) group.open = false;
        });
      },
      true
    );

    document.addEventListener("click", (event) => {
      if (!container.isConnected) return;
      container.querySelectorAll(".filter-group--bar[open]").forEach((group) => {
        if (!group.contains(event.target)) group.open = false;
      });
    });

    document.addEventListener("keydown", (event) => {
      if (!container.isConnected) return;

      if (event.key === "Escape") {
        if (drawerIsOpen()) {
          event.preventDefault();
          closeDrawer();
        } else {
          container.querySelectorAll(".filter-group--bar[open]").forEach((group) => {
            group.open = false;
          });
        }
        return;
      }

      if (event.key !== "Tab" || !drawerIsOpen()) return;

      const panel = drawer().querySelector(".filter-drawer__panel");
      const items = [...panel.querySelectorAll("a[href], button:not([disabled]), summary")].filter(
        (item) => item.offsetParent !== null
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener("popstate", () => {
      if (!container.isConnected) return;
      render(window.location.href, { push: false });
    });

    // Crossing to desktop with the drawer open would leave the body scroll-locked
    // behind the inline filter bar.
    window.matchMedia(DESKTOP).addEventListener("change", (event) => {
      if (event.matches && container.isConnected && drawerIsOpen()) {
        closeDrawer({ restoreFocus: false });
      }
    });
  };

  setup(document);
  document.addEventListener("shopify:section:load", (event) => setup(event.target));
})();
